import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Trash2,
  Check,
  Pencil,
  ArrowRightLeft,
  ChevronDown,
  Layers,
  Lock,
  ShieldCheck,
  Eye,
  EyeOff,
} from "lucide-react";

const API_URL = process.env.REACT_APP_API_URL || "";

function computeIsMobileLike() {
  if (typeof window === "undefined") return false;
  const narrow = window.matchMedia("(max-width: 1024px)").matches;
  if (narrow) return true;
  const ua = navigator.userAgent || "";
  const isIOS =
    /iPhone|iPod|iPad/.test(ua) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const isAndroidPhone = /Android/i.test(ua) && /Mobile/i.test(ua);
  if ((isIOS || isAndroidPhone) && navigator.maxTouchPoints > 0) return true;
  return false;
}

function formatEur(value, noSpace = false) {
  const n = typeof value === "number" ? value : parseFloat(value) || 0;
  const s = n.toLocaleString("el-GR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return noSpace ? s + "€" : s + " €";
}

const TAB_TITLE_EL = {
  transactions: "Συναλλαγές",
  fixed: "Πάγια",
  categories: "Κατηγορίες",
};

export default function FinanceApp() {
  const [isMobileLike, setIsMobileLike] = useState(computeIsMobileLike);

  useEffect(() => {
    const sync = () => setIsMobileLike(computeIsMobileLike());
    sync();
    const mq = window.matchMedia("(max-width: 1024px)");
    mq.addEventListener("change", sync);
    window.addEventListener("resize", sync);
    window.addEventListener("orientationchange", sync);
    return () => {
      mq.removeEventListener("change", sync);
      window.removeEventListener("resize", sync);
      window.removeEventListener("orientationchange", sync);
    };
  }, []);

  const [auth, setAuth] = useState(() => {
    if (typeof window === "undefined")
      return { token: "", role: "", email: "" };
    return {
      token: localStorage.getItem("auth_token") || "",
      role: localStorage.getItem("auth_role") || "",
      email: localStorage.getItem("auth_email") || "",
    };
  });
  const canEdit = auth.role === "edit";
  const readOnlyMobile = isMobileLike && !canEdit;
  const isAuthed = Boolean(auth.token);

  const apiFetch = useCallback(
    async (path, options = {}) => {
      const headers = {
        ...(options.headers || {}),
        ...(auth.token ? { Authorization: `Bearer ${auth.token}` } : {}),
      };
      return fetch(`${API_URL}${path}`, { ...options, headers });
    },
    [auth.token],
  );

  const logout = useCallback(() => {
    if (typeof window !== "undefined") {
      localStorage.removeItem("auth_token");
      localStorage.removeItem("auth_role");
      localStorage.removeItem("auth_email");
    }
    setAuth({ token: "", role: "", email: "" });
  }, []);

  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  });

  const [tab, setTab] = useState("transactions");
  const [transactions, setTransactions] = useState([]);
  const [transactionGroups, setTransactionGroups] = useState([]);
  const [expandedGroupIds, setExpandedGroupIds] = useState({});
  const [showAddGroup, setShowAddGroup] = useState(false);
  const [groupForm, setGroupForm] = useState({
    fixed_item_template_id: "",
    name: "",
    category_id: "",
    type: "έξοδο",
  });
  const [lineFormByGroup, setLineFormByGroup] = useState({});
  const [fixedItems, setFixedItems] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [accountBalances, setAccountBalances] = useState({});
  const [categories, setCategories] = useState({ εισόδημα: [], έξοδο: [] });
  const [summary, setSummary] = useState({});
  const [showAddTransaction, setShowAddTransaction] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);
  const [showAddFixed, setShowAddFixed] = useState(false);
  const [expandedFixedId, setExpandedFixedId] = useState(null);
  const [showEditAccount, setShowEditAccount] = useState(null);
  const [editingTransactionId, setEditingTransactionId] = useState(null);
  const [editingTransactionForm, setEditingTransactionForm] = useState({
    account_id: "",
    amount: "",
    due_date: "",
  });
  const [newCategory, setNewCategory] = useState("");
  const [loading, setLoading] = useState(true);
  const [pendingPayments, setPendingPayments] = useState({
    pending: [],
    overdue: [],
  });

  const [needsSetup, setNeedsSetup] = useState(null);
  const [setupForm, setSetupForm] = useState({
    email: "",
    password: "",
    passwordConfirm: "",
  });
  const [setupError, setSetupError] = useState("");
  const [setupLoading, setSetupLoading] = useState(false);

  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [authError, setAuthError] = useState("");

  const [transactionForm, setTransactionForm] = useState({
    account_id: "",
    category_id: "",
    type: "έξοδο",
    amount: "",
    description: "",
    fixed_item_template_id: "",
  });

  const [transferForm, setTransferForm] = useState({
    from_account_id: "",
    to_account_id: "",
    amount: "",
    description: "",
    transaction_date: "",
  });

  const [fixedForm, setFixedForm] = useState({
    account_id: "",
    category_id: "",
    type: "έξοδο",
    description: "",
    amount: "",
    frequency: "μηνιαία",
    amount_required: true,
    creates_group: false,
  });

  const [editAccountForm, setEditAccountForm] = useState({
    id: null,
    initial_balance: "",
    is_hide: false,
  });

  useEffect(() => {
    if (!isAuthed) {
      fetch(`${API_URL}/auth/setup-status`)
        .then((r) => r.json())
        .then((data) => setNeedsSetup(data.hasUsers === false))
        .catch(() => setNeedsSetup(false));
    }
  }, [isAuthed]);

  const handleSetup = async () => {
    setSetupError("");
    if (!setupForm.email || !setupForm.password) {
      setSetupError("Συμπληρώστε email και κωδικό");
      return;
    }
    if (setupForm.password.length < 6) {
      setSetupError("Ο κωδικός πρέπει να έχει τουλάχιστον 6 χαρακτήρες");
      return;
    }
    if (setupForm.password !== setupForm.passwordConfirm) {
      setSetupError("Οι κωδικοί δεν ταιριάζουν");
      return;
    }
    setSetupLoading(true);
    try {
      const res = await fetch(`${API_URL}/auth/setup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: setupForm.email,
          password: setupForm.password,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setSetupError(data.error || "Αποτυχία δημιουργίας χρήστη");
        return;
      }
      localStorage.setItem("auth_token", data.token);
      localStorage.setItem("auth_role", data.role);
      localStorage.setItem("auth_email", data.email);
      setAuth({ token: data.token, role: data.role, email: data.email });
      setNeedsSetup(false);
    } catch (err) {
      setSetupError(err.message);
    } finally {
      setSetupLoading(false);
    }
  };

  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }
  }, []);

  useEffect(() => {
    if (readOnlyMobile && tab !== "transactions") {
      setTab("transactions");
    }
  }, [readOnlyMobile, tab]);

  const fetchAccounts = async () => {
    try {
      const res = await apiFetch(`/accounts`);
      if (res.ok) {
        const data = await res.json();
        setAccounts(data);
        const balances = await Promise.all(
          data.map((acc) =>
            apiFetch(`/accounts/${acc.id}/balance`)
              .then((r) => r.json())
              .then((b) => ({ id: acc.id, balance: b.balance })),
          ),
        );
        setAccountBalances((prev) => {
          const next = { ...prev };
          balances.forEach(({ id, balance }) => {
            next[id] = balance;
          });
          return next;
        });
      }
    } catch (err) {
      console.log("Demo mode");
    }
  };

  const fetchTransactions = async () => {
    try {
      const res = await apiFetch(`/transactions/${currentMonth}`);
      if (!res.ok) return;
      const data = await res.json();
      if (Array.isArray(data)) {
        setTransactions(data);
        setTransactionGroups([]);
      } else {
        setTransactions(data.transactions ?? []);
        setTransactionGroups(data.groups ?? []);
      }
    } catch (err) {
      console.log("Demo mode");
    }
  };

  const fetchFixedItems = async () => {
    try {
      const res = await apiFetch(`/fixed-items?all=1`);
      if (res.ok) setFixedItems(await res.json());
    } catch (err) {
      console.log("Demo mode");
    }
  };

  const fetchCategories = async () => {
    try {
      const [incomeRes, expenseRes] = await Promise.all([
        apiFetch(`/categories/εισόδημα`),
        apiFetch(`/categories/έξοδο`),
      ]);
      if (incomeRes.ok && expenseRes.ok) {
        setCategories({
          εισόδημα: await incomeRes.json(),
          έξοδο: await expenseRes.json(),
        });
      }
    } catch (err) {
      console.log("Demo mode");
    }
  };

  const fetchSummary = async () => {
    try {
      const res = await apiFetch(`/month-summary/${currentMonth}`);
      if (res.ok) {
        const data = await res.json();
        setSummary(
          data.reduce((acc, item) => ({ ...acc, [item.type]: item }), {}),
        );
      }
    } catch (err) {
      console.log("Demo mode");
    }
  };

  const fetchPendingPayments = async () => {
    try {
      const res = await apiFetch(`/pending-payments`);
      if (res.ok) {
        const data = await res.json();
        setPendingPayments(data);
      }
    } catch (err) {
      console.log("Demo mode");
    }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchAccounts(),
        fetchTransactions(),
        fetchFixedItems(),
        fetchCategories(),
        fetchSummary(),
        fetchPendingPayments(),
      ]);
    } finally {
      setLoading(false);
    }
    // Refetch only when currentMonth changes; fetch* use it from closure
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMonth]);

  useEffect(() => {
    if (!isAuthed) return;
    fetchData();
  }, [fetchData, isAuthed]);

  useEffect(() => {
    if (!readOnlyMobile) return;
    setShowAddTransaction(false);
    setShowAddFixed(false);
    setShowAddGroup(false);
    setShowTransfer(false);
    setShowEditAccount(null);
    setEditingTransactionId(null);
  }, [readOnlyMobile]);

  const denyMutation = () => {
    window.alert("Δεν έχετε δικαίωμα επεξεργασίας (readonly).");
    return true;
  };

  const handleLogin = async () => {
    setAuthError("");
    try {
      const res = await fetch(`${API_URL}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: loginForm.email,
          password: loginForm.password,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setAuthError(data.error || "Αποτυχία σύνδεσης");
        return;
      }
      localStorage.setItem("auth_token", data.token);
      localStorage.setItem("auth_role", data.role);
      localStorage.setItem("auth_email", data.email);
      setAuth({ token: data.token, role: data.role, email: data.email });
      setLoginForm({ email: "", password: "" });
      await fetchData();
    } catch (err) {
      setAuthError(err.message);
    }
  };

  // const sendNotification = (title, message, type = "info") => {
  //   if ("Notification" in window && Notification.permission === "granted") {
  //     new Notification(title, {
  //       body: message,
  //       icon: type === "warning" ? "⚠️" : "📅",
  //       tag: "finance-app",
  //       requireInteraction: type === "warning",
  //     });
  //   }
  // };

  /** Σημερινή ημερομηνία (τοπική) — μεταφορές & νέες γραμμές ομάδων */
  const defaultGroupLineDate = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  };

  const defaultTransferDate = defaultGroupLineDate;

  const handleTransfer = async () => {
    if (readOnlyMobile && denyMutation()) return;
    const amt = parseFloat(transferForm.amount);
    if (
      !transferForm.from_account_id ||
      !transferForm.to_account_id ||
      !Number.isFinite(amt) ||
      amt <= 0
    ) {
      window.alert("Επιλέξτε λογαριασμούς και έγκυρο ποσό");
      return;
    }
    if (transferForm.from_account_id === transferForm.to_account_id) {
      window.alert("Ο αποστολέας και ο παραλήπτης πρέπει να διαφέρουν");
      return;
    }
    try {
      const res = await apiFetch(`/transfers`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          from_account_id: parseInt(transferForm.from_account_id, 10),
          to_account_id: parseInt(transferForm.to_account_id, 10),
          amount: amt,
          description: transferForm.description || undefined,
          transaction_date:
            transferForm.transaction_date || defaultTransferDate(),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        window.alert(data.error || "Αποτυχία μεταφοράς");
        return;
      }
      setTransferForm({
        from_account_id: "",
        to_account_id: "",
        amount: "",
        description: "",
        transaction_date: "",
      });
      setShowTransfer(false);
      await Promise.all([fetchTransactions(), fetchSummary(), fetchAccounts()]);
    } catch (err) {
      window.alert("Σφάλμα: " + err.message);
    }
  };

  const handleAddTransaction = async () => {
    if (readOnlyMobile && denyMutation()) return;
    if (!transactionForm.category_id || !transactionForm.amount) {
      window.alert("Συμπληρώστε όλα τα πεδία");
      return;
    }

    try {
      const payload = {
        account_id: transactionForm.account_id || null,
        category_id: transactionForm.category_id,
        type: transactionForm.type,
        amount: transactionForm.amount,
        description: transactionForm.description,
      };
      const res = await apiFetch(`/transactions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        setTransactionForm({
          account_id: "",
          category_id: "",
          type: "έξοδο",
          amount: "",
          description: "",
          fixed_item_template_id: "",
        });
        setShowAddTransaction(false);
        fetchTransactions();
        fetchSummary();
        fetchAccounts();
      }
    } catch (err) {
      window.alert("Σφάλμα: " + err.message);
    }
  };

  const handleAddFixed = async () => {
    if (readOnlyMobile && denyMutation()) return;
    if (!fixedForm.category_id || !fixedForm.description) {
      window.alert("Συμπληρώστε όλα τα πεδία");
      return;
    }

    try {
      const res = await apiFetch(`/fixed-items`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(fixedForm),
      });
      if (res.ok) {
        setFixedForm({
          account_id: "",
          category_id: "",
          type: "έξοδο",
          description: "",
          amount: "",
          frequency: "μηνιαία",
          amount_required: true,
          creates_group: false,
        });
        setShowAddFixed(false);
        fetchFixedItems();
      }
    } catch (err) {
      window.alert("Σφάλμα: " + err.message);
    }
  };

  const groupFixedTemplates = useMemo(() => {
    const usedFixedIds = new Set(
      transactionGroups
        .map((g) => g.fixed_item_id)
        .filter((id) => id != null && id !== "")
        .map((id) => Number(id)),
    );
    return fixedItems.filter(
      (i) =>
        i.is_active &&
        (i.creates_group === true ||
          i.creates_group === 1 ||
          i.creates_group === "1") &&
        !usedFixedIds.has(Number(i.id)),
    );
  }, [fixedItems, transactionGroups]);

  const transactionFixedTemplates = useMemo(() => {
    const usedKeys = new Set(
      transactions.map(
        (t) =>
          `${String(t.description || "")
            .trim()
            .toLowerCase()}|${t.category_id}|${t.type}`,
      ),
    );
    return fixedItems.filter(
      (i) =>
        i.is_active &&
        !(
          i.creates_group === true ||
          i.creates_group === 1 ||
          i.creates_group === "1"
        ) &&
        !usedKeys.has(
          `${String(i.description || "")
            .trim()
            .toLowerCase()}|${i.category_id}|${i.type}`,
        ),
    );
  }, [fixedItems, transactions]);

  const mergedMonthRows = useMemo(() => {
    const txSortLabel = (t) =>
      String(t.description || t.Category?.name || "")
        .trim()
        .toLocaleLowerCase("el");
    const groupSortLabel = (g) =>
      String(g.name || "")
        .trim()
        .toLocaleLowerCase("el");
    /** 0: εισόδημα tx, 1: εισόδημα ομάδα, 2: έξοδο ομάδα, 3: έξοδο tx */
    const priority = (row) => {
      if (row.kind === "tx") {
        return row.tx.type === "εισόδημα" ? 0 : 3;
      }
      return row.group.type === "εισόδημα" ? 1 : 2;
    };
    const label = (row) =>
      row.kind === "tx" ? txSortLabel(row.tx) : groupSortLabel(row.group);

    const txRows = transactions.map((t) => ({ kind: "tx", tx: t }));
    const grRows = transactionGroups.map((g) => ({ kind: "group", group: g }));
    return [...txRows, ...grRows].sort((a, b) => {
      const pa = priority(a);
      const pb = priority(b);
      if (pa !== pb) return pa - pb;
      return label(a).localeCompare(label(b), "el", { sensitivity: "base" });
    });
  }, [transactions, transactionGroups]);

  const handleCreateGroup = async () => {
    if (readOnlyMobile && denyMutation()) return;
    if (!groupForm.name.trim() || !groupForm.category_id) {
      window.alert("Όνομα ομάδας και κατηγορία απαιτούνται");
      return;
    }
    try {
      const res = await apiFetch(`/transaction-groups`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          month_year: currentMonth,
          name: groupForm.name.trim(),
          category_id: parseInt(groupForm.category_id, 10),
          type: groupForm.type,
          ...(groupForm.fixed_item_template_id
            ? {
                fixed_item_id: parseInt(groupForm.fixed_item_template_id, 10),
              }
            : {}),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        window.alert(data.error || "Αποτυχία");
        return;
      }
      setGroupForm({
        fixed_item_template_id: "",
        name: "",
        category_id: "",
        type: "έξοδο",
      });
      setShowAddGroup(false);
      if (data.id) {
        setExpandedGroupIds((e) => ({ ...e, [data.id]: true }));
        setLineFormByGroup((lf) => ({
          ...lf,
          [data.id]: {
            account_id: "",
            amount: "",
            description: "",
            transaction_date: defaultGroupLineDate(),
          },
        }));
      }
      await Promise.all([fetchTransactions(), fetchSummary(), fetchAccounts()]);
    } catch (err) {
      window.alert("Σφάλμα: " + err.message);
    }
  };

  const handleAddGroupLine = async (groupId) => {
    if (readOnlyMobile && denyMutation()) return;
    const draft = lineFormByGroup[groupId] || {};
    const amt = parseFloat(draft.amount);
    if (!draft.account_id || !Number.isFinite(amt) || amt <= 0) {
      window.alert("Λογαριασμός και ποσό απαιτούνται");
      return;
    }
    try {
      const res = await apiFetch(`/transaction-groups/${groupId}/lines`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          account_id: parseInt(draft.account_id, 10),
          amount: amt,
          description: draft.description || undefined,
          transaction_date: draft.transaction_date || defaultGroupLineDate(),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        window.alert(data.error || "Αποτυχία");
        return;
      }
      setLineFormByGroup((lf) => ({
        ...lf,
        [groupId]: {
          account_id: "",
          amount: "",
          description: "",
          transaction_date: defaultGroupLineDate(),
        },
      }));
      await Promise.all([fetchTransactions(), fetchSummary(), fetchAccounts()]);
    } catch (err) {
      window.alert("Σφάλμα: " + err.message);
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (readOnlyMobile && denyMutation()) return;
    try {
      const res = await apiFetch(`/transaction-groups/${groupId}`, {
        method: "DELETE",
      });
      if (!res.ok) {
        window.alert("Αποτυχία διαγραφής");
        return;
      }
      await Promise.all([fetchTransactions(), fetchSummary(), fetchAccounts()]);
    } catch (err) {
      window.alert("Σφάλμα: " + err.message);
    }
  };

  const toggleGroupExpanded = (groupId) => {
    setExpandedGroupIds((prev) => {
      const next = { ...prev };
      if (next[groupId]) delete next[groupId];
      else {
        next[groupId] = true;
        setLineFormByGroup((lf) => ({
          ...lf,
          [groupId]: lf[groupId] || {
            account_id: "",
            amount: "",
            description: "",
            transaction_date: defaultGroupLineDate(),
          },
        }));
      }
      return next;
    });
  };

  const toggleTransactionDone = async (id) => {
    if (readOnlyMobile && denyMutation()) return;
    try {
      const res = await apiFetch(`/transactions/${id}/done`, {
        method: "PUT",
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        window.alert(data.error || "Αποτυχία ενημέρωσης");
        return;
      }
      await Promise.all([fetchTransactions(), fetchSummary(), fetchAccounts()]);
    } catch (err) {
      window.alert("Σφάλμα: " + err.message);
    }
  };

  const deleteTransaction = async (id) => {
    if (readOnlyMobile && denyMutation()) return;
    const row = transactions.find((x) => x.id === id);
    const confirmMsg = row?.transfer_group_id
      ? "Θα διαγραφούν και οι δύο εγγραφές της μεταφοράς και θα διορθωθούν τα υπόλοιπα. Συνέχεια;"
      : "Σίγουρα;";
    if (window.confirm(confirmMsg)) {
      try {
        await apiFetch(`/transactions/${id}`, { method: "DELETE" });
        fetchTransactions();
        fetchSummary();
        fetchAccounts();
      } catch (err) {
        window.alert("Σφάλμα: " + err.message);
      }
    }
  };

  const startEditTransaction = (t) => {
    if (readOnlyMobile) return;
    if (t.transfer_group_id) {
      window.alert(
        "Οι μεταφορές δεν επεξεργάζονται εδώ — διαγράψτε τις και δημιουργήστε νέα.",
      );
      return;
    }
    setEditingTransactionId(t.id);
    setEditingTransactionForm({
      account_id: t.account_id ? String(t.account_id) : "",
      amount: t.amount != null ? String(t.amount) : "",
      due_date: t.due_date ? String(t.due_date).slice(0, 10) : "",
    });
  };

  const handleUpdateTransaction = async (id) => {
    if (readOnlyMobile && denyMutation()) return;
    try {
      const { account_id, amount, due_date } = editingTransactionForm;
      const res = await apiFetch(`/transactions/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          account_id: account_id || null,
          amount: amount === "" ? 0 : parseFloat(amount),
          due_date: due_date || null,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        window.alert(data.error || "Σφάλμα ενημέρωσης");
        return;
      }
      setEditingTransactionId(null);
      await fetchTransactions();
      await fetchAccounts();
    } catch (err) {
      window.alert("Σφάλμα: " + err.message);
    }
  };

  const toggleFixedItemActive = async (item) => {
    if (readOnlyMobile && denyMutation()) return;
    try {
      const res = await apiFetch(`/fixed-items/${item.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ is_active: !item.is_active }),
      });
      if (!res.ok) throw new Error("Αποτυχία ενημέρωσης");
      await fetchFixedItems();
    } catch (err) {
      window.alert("Σφάλμα: " + err.message);
    }
  };

  const deleteFixedItem = async (id) => {
    if (readOnlyMobile && denyMutation()) return;
    if (!window.confirm("Διαγραφή παγίου; Η ενέργεια δεν αναιρείται.")) return;
    try {
      const res = await apiFetch(`/fixed-items/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Αποτυχία διαγραφής");
      await fetchFixedItems();
    } catch (err) {
      window.alert("Σφάλμα: " + err.message);
    }
  };

  const updateFixedItemField = async (id, updates) => {
    if (readOnlyMobile && denyMutation()) return;
    try {
      const res = await apiFetch(`/fixed-items/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updates),
      });
      if (!res.ok) throw new Error("Αποτυχία ενημέρωσης");
      await fetchFixedItems();
    } catch (err) {
      window.alert("Σφάλμα: " + err.message);
    }
  };

  const handleAddCategory = async (type) => {
    if (readOnlyMobile && denyMutation()) return;
    if (!newCategory.trim()) return;
    try {
      const res = await apiFetch(`/categories`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: newCategory, type }),
      });
      if (res.ok) {
        setNewCategory("");
        fetchCategories();
      }
    } catch (err) {
      window.alert("Σφάλμα: " + err.message);
    }
  };

  const handleCloseMonth = async () => {
    if (readOnlyMobile && denyMutation()) return;
    if (
      window.confirm(
        "Θέλετε να κλείσετε τον μήνα; Θα δημιουργηθούν συναλλαγές από τα σταθερά μόνο αν ο μήνας δεν έχει καθόλου συναλλαγές.",
      )
    ) {
      try {
        const res = await apiFetch(`/close-month`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ monthYear: currentMonth }),
        });
        const data = await res.json().catch(() => ({}));
        if (res.ok) {
          window.alert("✅ Μήνας κλείστηκε με επιτυχία!");
          fetchTransactions();
        } else {
          window.alert(
            data.error || "Σφάλμα: " + (data.message || res.statusText),
          );
        }
      } catch (err) {
        window.alert("Σφάλμα: " + err.message);
      }
    }
  };

  const startEditAccount = (account) => {
    if (readOnlyMobile) return;
    setEditAccountForm({
      id: account.id,
      initial_balance: account.initial_balance,
      is_hide: account.is_hide || false,
    });
    setShowEditAccount(account.id);
  };

  const handleUpdateAccount = async () => {
    if (readOnlyMobile && denyMutation()) return;
    try {
      const res = await apiFetch(`/accounts/${editAccountForm.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          initial_balance: editAccountForm.initial_balance,
          is_hide: editAccountForm.is_hide,
        }),
      });
      if (res.ok) {
        setShowEditAccount(null);
        fetchAccounts();
      }
    } catch (err) {
      window.alert("Σφάλμα: " + err.message);
    }
  };

  const toggleAccountHide = async (account, e) => {
    e.stopPropagation();
    if (readOnlyMobile && denyMutation()) return;
    try {
      const res = await apiFetch(`/accounts/${account.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ is_hide: !account.is_hide }),
      });
      if (res.ok) fetchAccounts();
    } catch (err) {
      window.alert("Σφάλμα: " + err.message);
    }
  };

  const previousMonth = () => {
    const [year, month] = currentMonth.split("-");
    const prevMonth =
      parseInt(month) === 1
        ? `${parseInt(year) - 1}-12`
        : `${year}-${String(parseInt(month) - 1).padStart(2, "0")}`;
    setCurrentMonth(prevMonth);
  };

  const nextMonth = () => {
    const [year, month] = currentMonth.split("-");
    const nextM =
      parseInt(month) === 12
        ? `${parseInt(year) + 1}-01`
        : `${year}-${String(parseInt(month) + 1).padStart(2, "0")}`;
    setCurrentMonth(nextM);
  };

  const now = new Date();
  const todayMonthStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const isViewingCurrentMonth = currentMonth === todayMonthStr;

  if (isAuthed && loading)
    return <div className="p-4 text-center">Φόρτωση...</div>;

  const pendingIncomeTransactions = transactions.filter(
    (t) => t.type === "εισόδημα" && !t.is_done,
  );
  const pendingExpenseTransactions = transactions.filter(
    (t) => t.type === "έξοδο" && !t.is_done,
  );
  const pendingIncomeSum = pendingIncomeTransactions.reduce(
    (s, t) => s + parseFloat(t.amount || 0),
    0,
  );
  const pendingExpenseSum = pendingExpenseTransactions.reduce(
    (s, t) => s + parseFloat(t.amount || 0),
    0,
  );
  const completedIncomeTransactions = transactions.filter(
    (t) => t.type === "εισόδημα" && t.is_done,
  );
  const completedExpenseTransactions = transactions.filter(
    (t) => t.type === "έξοδο" && t.is_done,
  );
  const incomeGroupSum = transactionGroups
    .filter((g) => g.type === "εισόδημα")
    .reduce((s, g) => s + parseFloat(g.total_amount || 0), 0);
  const expenseGroupSum = transactionGroups
    .filter((g) => g.type === "έξοδο")
    .reduce((s, g) => s + parseFloat(g.total_amount || 0), 0);
  const completedIncomeSum =
    completedIncomeTransactions.reduce(
      (s, t) => s + parseFloat(t.amount || 0),
      0,
    ) + incomeGroupSum ||
    Number(summary.εισόδημα?.completed) ||
    0;
  const completedExpenseSum =
    completedExpenseTransactions.reduce(
      (s, t) => s + parseFloat(t.amount || 0),
      0,
    ) + expenseGroupSum ||
    Number(summary.έξοδο?.completed) ||
    0;
  const [yearStr, monthStr] = currentMonth.split("-");
  const daysInMonth = new Date(
    parseInt(yearStr),
    parseInt(monthStr),
    0,
  ).getDate();
  const monthStart = `${yearStr}-${monthStr}-01`;
  const monthEnd = `${yearStr}-${monthStr}-${String(daysInMonth).padStart(2, "0")}`;
  const totalBalance = accounts
    .filter((acc) => !acc.is_hide)
    .reduce(
      (sum, acc) => sum + parseFloat(accountBalances[acc.id] || 0),
      0,
    );

  if (!isAuthed && needsSetup === null) {
    return <div className="p-4 text-center">Φόρτωση...</div>;
  }

  if (!isAuthed && needsSetup) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4 font-sans">
        <div className="w-full max-w-sm bg-white rounded-xl shadow-lg border border-gray-100 p-6 space-y-4">
          <div className="text-center space-y-1">
            <p className="text-3xl">💰</p>
            <h2 className="text-xl font-bold text-gray-800">Αρχική Ρύθμιση</h2>
            <p className="text-sm text-gray-500">
              Δημιουργήστε τον πρώτο χρήστη για να ξεκινήσετε
            </p>
          </div>
          <input
            type="email"
            placeholder="Email"
            value={setupForm.email}
            onChange={(e) =>
              setSetupForm((f) => ({ ...f, email: e.target.value }))
            }
            onKeyDown={(e) => e.key === "Enter" && handleSetup()}
            className="w-full border border-gray-300 rounded px-3 py-2"
          />
          <input
            type="password"
            placeholder="Κωδικός"
            value={setupForm.password}
            onChange={(e) =>
              setSetupForm((f) => ({ ...f, password: e.target.value }))
            }
            onKeyDown={(e) => e.key === "Enter" && handleSetup()}
            className="w-full border border-gray-300 rounded px-3 py-2"
          />
          <input
            type="password"
            placeholder="Επιβεβαίωση κωδικού"
            value={setupForm.passwordConfirm}
            onChange={(e) =>
              setSetupForm((f) => ({ ...f, passwordConfirm: e.target.value }))
            }
            onKeyDown={(e) => e.key === "Enter" && handleSetup()}
            className="w-full border border-gray-300 rounded px-3 py-2"
          />
          {setupError && (
            <p className="text-sm text-red-600 font-medium">{setupError}</p>
          )}
          <button
            type="button"
            onClick={handleSetup}
            disabled={setupLoading}
            className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white py-2.5 rounded font-bold"
          >
            {setupLoading ? "Δημιουργία..." : "Δημιουργία & Είσοδος"}
          </button>
        </div>
      </div>
    );
  }

  return !isAuthed ? (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4 font-sans">
      <div className="w-full max-w-sm bg-white rounded-xl shadow-sm border border-gray-100 p-5 space-y-3">
        <h2 className="text-lg font-bold">Σύνδεση</h2>
        <input
          type="email"
          placeholder="Email"
          value={loginForm.email}
          onChange={(e) =>
            setLoginForm((f) => ({ ...f, email: e.target.value }))
          }
          className="w-full border border-gray-300 rounded px-3 py-2"
        />
        <input
          type="password"
          placeholder="Password"
          value={loginForm.password}
          onChange={(e) =>
            setLoginForm((f) => ({ ...f, password: e.target.value }))
          }
          className="w-full border border-gray-300 rounded px-3 py-2"
        />
        {authError && (
          <p className="text-sm text-red-600 font-medium">{authError}</p>
        )}
        <button
          type="button"
          onClick={handleLogin}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2.5 rounded font-bold"
        >
          Σύνδεση
        </button>
      </div>
    </div>
  ) : (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 pb-20 font-sans">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-4 sticky top-0 z-10">
        <div className="max-w-md mx-auto">
          <div className="flex items-center justify-between gap-3 mb-4">
            <h1 className="text-2xl font-bold leading-none">💰 Ταμείο</h1>
            <div className="flex items-center gap-2 text-xs text-white/90 shrink-0">
              <span className="max-w-[10rem] sm:max-w-[14rem] truncate flex items-center gap-1">
                <span className="truncate">{auth.email}</span>
                {auth.role === "readonly" ? (
                  <Lock
                    className="w-3.5 h-3.5"
                    aria-label="Readonly"
                    title="Readonly"
                  />
                ) : auth.role === "edit" ? (
                  <ShieldCheck
                    className="w-3.5 h-3.5"
                    aria-label="Edit"
                    title="Edit"
                  />
                ) : null}
              </span>
              <button
                type="button"
                onClick={logout}
                className="px-2 py-1 rounded bg-white/10 hover:bg-white/20 leading-none"
              >
                Logout
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between bg-white bg-opacity-20 rounded-lg p-3">
            <button
              onClick={previousMonth}
              className="p-2 hover:bg-white hover:bg-opacity-10 rounded"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <span className="text-lg font-semibold">{currentMonth}</span>
            <button
              onClick={nextMonth}
              disabled={isViewingCurrentMonth}
              className="p-2 hover:bg-white hover:bg-opacity-10 rounded disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-md mx-auto p-4 space-y-4">
        {/* Total Balance */}
        <div className="bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg p-4 text-center">
          <p className="text-sm font-semibold opacity-90">Συνολό Υπόλοιπο</p>
          <p className="text-4xl font-bold">{formatEur(totalBalance)}</p>
        </div>

        {/* Accounts */}
        <div className="space-y-2">
          <p className="text-xs font-semibold text-gray-600 px-1">
            💳 ΛΟΓΑΡΙΑΣΜΟΙ
          </p>
          {accounts.map((acc) => (
            <div key={acc.id}>
              {showEditAccount === acc.id ? (
                <div className="bg-white rounded-lg p-4 space-y-3 border-2 border-blue-400">
                  <p className="font-semibold text-sm">{acc.name}</p>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="Τρέχον υπόλοιπο"
                    value={editAccountForm.initial_balance}
                    onChange={(e) =>
                      setEditAccountForm({
                        ...editAccountForm,
                        initial_balance: e.target.value,
                      })
                    }
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  />
                  <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editAccountForm.is_hide}
                      onChange={(e) =>
                        setEditAccountForm({
                          ...editAccountForm,
                          is_hide: e.target.checked,
                        })
                      }
                      className="rounded"
                    />
                    Απόκρυψη από σύνολο
                  </label>
                  <div className="flex gap-2">
                    <button
                      onClick={handleUpdateAccount}
                      className="flex-1 bg-blue-600 text-white py-2 rounded font-bold text-sm"
                    >
                      ✅ Αποθήκευση
                    </button>
                    <button
                      onClick={() => setShowEditAccount(null)}
                      className="flex-1 bg-gray-300 text-gray-700 py-2 rounded font-bold text-sm"
                    >
                      ✕ Ακύρωση
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  onClick={() => !readOnlyMobile && startEditAccount(acc)}
                  className={`rounded-lg px-3 py-2 border-l-4 ${
                    acc.is_hide
                      ? "bg-gray-100 border-gray-300 opacity-60"
                      : "bg-white border-gray-500"
                  } ${readOnlyMobile ? "" : "cursor-pointer hover:shadow-md transition"}`}
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <p
                        className={`font-semibold text-sm flex items-center gap-2 ${
                          acc.is_hide ? "text-gray-400" : ""
                        }`}
                      >
                        💳 {acc.name}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {!readOnlyMobile && (
                        <button
                          type="button"
                          onClick={(e) => toggleAccountHide(acc, e)}
                          className={`p-1 rounded hover:bg-gray-200 ${
                            acc.is_hide ? "text-gray-400" : "text-gray-500"
                          }`}
                          title={
                            acc.is_hide
                              ? "Εμφάνιση στο σύνολο"
                              : "Απόκρυψη από σύνολο"
                          }
                        >
                          {acc.is_hide ? (
                            <EyeOff className="w-4 h-4" />
                          ) : (
                            <Eye className="w-4 h-4" />
                          )}
                        </button>
                      )}
                      <p
                        className={`text-base font-bold ${
                          acc.is_hide ? "text-gray-400" : "text-blue-600"
                        }`}
                      >
                        {formatEur(accountBalances[acc.id] || 0)}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 gap-3 pt-4">
          <div className="bg-green-50 border-l-4 border-green-500 p-2 rounded-lg">
            <p className="text-xs text-gray-600 font-semibold">ΕΙΣΟΔΗΜΑΤΑ</p>
            <p className="text-2xl font-bold text-green-600">
              {formatEur(completedIncomeSum)}
            </p>
            <p className="text-sm text-green-700 mt-1 font-medium">
              ⏳ {formatEur(pendingIncomeSum)}
            </p>
            {pendingIncomeTransactions.length > 0 && (
              <ul className="mt-1 space-y-0.5 text-xs text-green-800">
                {pendingIncomeTransactions.map((t) => (
                  <li
                    key={t.id}
                    className="flex justify-between items-baseline gap-2"
                  >
                    <span className="min-w-0 truncate">
                      {t.description || t.Category?.name}
                    </span>
                    <span className="font-medium whitespace-nowrap shrink-0">
                      +{formatEur(t.amount, true)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="bg-red-50 border-l-4 border-red-500 p-2 rounded-lg">
            <p className="text-xs text-gray-600 font-semibold">ΕΞΟΔΑ</p>
            <p className="text-2xl font-bold text-red-600">
              {formatEur(completedExpenseSum)}
            </p>
            <p className="text-sm text-red-700 mt-1 font-medium">
              ⏳ {formatEur(pendingExpenseSum)}
            </p>
            {pendingExpenseTransactions.filter(
              (t) => parseFloat(t.amount || 0) > 0,
            ).length > 0 && (
              <ul className="mt-1 space-y-0.5 text-xs text-red-800">
                {pendingExpenseTransactions
                  .filter((t) => parseFloat(t.amount || 0) > 0)
                  .map((t) => {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const due = t.due_date ? new Date(t.due_date) : null;
                    if (due) due.setHours(0, 0, 0, 0);
                    const diffDays = due
                      ? Math.ceil((due - today) / (1000 * 60 * 60 * 24))
                      : null;
                    const isOverdueOrToday = diffDays != null && diffDays <= 0;
                    const isWithin3Days =
                      diffDays != null && diffDays > 0 && diffDays <= 3;
                    const dueDateClass = isOverdueOrToday
                      ? "text-red-600 font-bold"
                      : isWithin3Days
                        ? "text-orange-500 font-medium"
                        : "";
                    return (
                      <li
                        key={t.id}
                        className={`flex justify-between items-baseline gap-2 ${dueDateClass}`}
                      >
                        <span className="min-w-0 truncate">
                          {t.description || t.Category?.name}
                        </span>
                        <span className="font-medium whitespace-nowrap shrink-0">
                          -{formatEur(t.amount, true)}
                        </span>
                      </li>
                    );
                  })}
              </ul>
            )}
          </div>
        </div>

        {/* Payment Notifications */}
        {pendingPayments.overdue && pendingPayments.overdue.length > 0 && (
          <div className="bg-red-100 border-l-4 border-red-500 p-4 rounded-lg">
            <p className="font-bold text-red-800">
              ⚠️ {pendingPayments.overdue.length} Καθυστερημένες
            </p>
          </div>
        )}

        {pendingPayments.pending && pendingPayments.pending.length > 0 && (
          <div className="bg-yellow-100 border-l-4 border-yellow-500 p-4 rounded-lg">
            <p className="font-bold text-yellow-800">
              📅 {pendingPayments.pending.length} Επικείμενες
            </p>
          </div>
        )}

        {/* Tabs + section title (hidden on mobile read-only) */}
        {!readOnlyMobile && (
          <div className="flex items-center gap-2 bg-white rounded-lg p-1 pr-2 sticky top-20 z-10 mb-3 shadow-sm border border-gray-100">
            <div className="flex gap-2 overflow-x-auto flex-1 min-w-0 py-0.5 pl-1">
              {["transactions", "fixed", "categories"].map((t) => (
                <button
                  key={t}
                  onClick={() => setTab(t)}
                  className={`py-2 px-3 rounded text-sm font-semibold transition whitespace-nowrap shrink-0 ${
                    tab === t
                      ? "bg-blue-600 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {t === "transactions" && "✅"}
                  {t === "fixed" && "📌"}
                  {t === "categories" && "🏷️"}
                </button>
              ))}
            </div>
            <div
              className="shrink-0 flex items-center min-h-[2.5rem] pl-3 ml-0.5 border-l border-gray-200"
              role="status"
              aria-live="polite"
            >
              <span
                className={`text-sm sm:text-[0.95rem] font-bold leading-tight ${
                  tab === "transactions"
                    ? "text-sky-800"
                    : tab === "fixed"
                      ? "text-amber-800"
                      : "text-violet-800"
                }`}
              >
                {TAB_TITLE_EL[tab] ?? tab}
              </span>
            </div>
          </div>
        )}

        {/* Transactions Tab */}
        {tab === "transactions" && (
          <div className="space-y-4">
            {mergedMonthRows.length === 0 && !readOnlyMobile && (
              <div className="flex flex-col gap-2">
                <button
                  onClick={handleCloseMonth}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white py-3 rounded-lg font-bold"
                >
                  🔐 Κλείσιμο Μήνα
                </button>
                {accounts.length >= 2 && (
                  <button
                    type="button"
                    onClick={() => {
                      setShowTransfer(true);
                      setShowAddTransaction(false);
                      setTransferForm((prev) => ({
                        ...prev,
                        transaction_date:
                          prev.transaction_date || defaultTransferDate(),
                      }));
                    }}
                    className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-3 rounded-lg font-bold flex items-center justify-center gap-2"
                  >
                    <ArrowRightLeft className="w-5 h-5" />
                    Μεταφορά χρημάτων
                  </button>
                )}
              </div>
            )}
            <div className="space-y-2">
              {mergedMonthRows.map((row) => {
                if (row.kind === "group") {
                  const g = row.group;
                  const expanded = Boolean(expandedGroupIds[g.id]);
                  const draft = lineFormByGroup[g.id] || {
                    account_id: "",
                    amount: "",
                    description: "",
                    transaction_date: defaultGroupLineDate(),
                  };
                  return (
                    <div
                      key={`grp-${g.id}`}
                      className={`bg-white rounded-lg border-l-4 overflow-hidden ${
                        g.type === "εισόδημα"
                          ? "border-green-600"
                          : "border-red-600"
                      }`}
                    >
                      <div className="px-3 py-2 flex justify-between items-start gap-2">
                        <button
                          type="button"
                          onClick={() => toggleGroupExpanded(g.id)}
                          className="flex-1 min-w-0 text-left flex items-start gap-2"
                        >
                          {expanded ? (
                            <ChevronDown className="w-4 h-4 shrink-0 mt-0.5 text-gray-500" />
                          ) : (
                            <ChevronRight className="w-4 h-4 shrink-0 mt-0.5 text-gray-500" />
                          )}
                          <span className="min-w-0">
                            <span className="flex items-center gap-1.5 font-semibold text-sm">
                              <Layers className="w-4 h-4 shrink-0 text-indigo-600" />
                              {g.name}
                            </span>
                            <span className="text-xs text-gray-600 block mt-0.5">
                              {g.Category?.name} ·{" "}
                              {g.line_count ?? g.lines?.length ?? 0} προσθήκες
                            </span>
                          </span>
                        </button>
                        <div className="text-right shrink-0">
                          <p
                            className={`text-sm font-bold ${g.type === "εισόδημα" ? "text-green-600" : "text-red-600"}`}
                          >
                            {g.type === "εισόδημα" ? "+" : "-"}
                            {formatEur(parseFloat(g.total_amount) || 0)}
                          </p>
                          {!readOnlyMobile &&
                            (g.line_count ?? g.lines?.length ?? 0) === 0 && (
                            <button
                              type="button"
                              onClick={() => handleDeleteGroup(g.id)}
                              className="p-1 hover:bg-red-50 rounded mt-1"
                              title="Διαγραφή ομάδας"
                            >
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </button>
                          )}
                        </div>
                      </div>
                      {expanded && (
                        <div className="bg-gray-50 border-t border-gray-100 px-3 py-2 space-y-2">
                          {(g.lines || []).map((line) => (
                            <div
                              key={line.id}
                              className="flex justify-between items-start gap-2 text-xs border-b border-dashed border-gray-200 pb-2 last:border-0"
                            >
                              <div className="min-w-0">
                                <p className="font-medium text-gray-800">
                                  {line.description || "—"}
                                </p>
                                <p className="text-gray-500">
                                  {line.Account?.name || "—"} ·{" "}
                                  {new Date(
                                    line.transaction_date,
                                  ).toLocaleDateString("el-GR")}
                                </p>
                              </div>
                              <div className="flex items-center gap-1 shrink-0">
                                <span
                                  className={`font-bold ${g.type === "εισόδημα" ? "text-green-600" : "text-red-600"}`}
                                >
                                  {g.type === "εισόδημα" ? "+" : "-"}
                                  {formatEur(line.amount)}
                                </span>
                                {!readOnlyMobile && (
                                  <button
                                    type="button"
                                    onClick={() => deleteTransaction(line.id)}
                                    className="p-1 hover:bg-red-50 rounded"
                                    title="Διαγραφή"
                                  >
                                    <Trash2 className="w-3.5 h-3.5 text-red-600" />
                                  </button>
                                )}
                              </div>
                            </div>
                          ))}
                          {!readOnlyMobile && (
                            <div className="pt-1 space-y-2 border-t border-gray-200">
                              <p className="text-xs font-semibold text-gray-700">
                                Νέα προσθήκη
                              </p>
                              <select
                                value={draft.account_id}
                                onChange={(e) =>
                                  setLineFormByGroup((lf) => ({
                                    ...lf,
                                    [g.id]: {
                                      ...draft,
                                      account_id: e.target.value,
                                    },
                                  }))
                                }
                                className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                              >
                                <option value="">Λογαριασμός</option>
                                {accounts.map((acc) => (
                                  <option key={acc.id} value={acc.id}>
                                    {acc.name}
                                  </option>
                                ))}
                              </select>
                              <input
                                type="number"
                                step="0.01"
                                placeholder="Ποσό"
                                value={draft.amount}
                                onChange={(e) =>
                                  setLineFormByGroup((lf) => ({
                                    ...lf,
                                    [g.id]: {
                                      ...draft,
                                      amount: e.target.value,
                                    },
                                  }))
                                }
                                className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                              />
                              <input
                                type="date"
                                value={draft.transaction_date}
                                onChange={(e) =>
                                  setLineFormByGroup((lf) => ({
                                    ...lf,
                                    [g.id]: {
                                      ...draft,
                                      transaction_date: e.target.value,
                                    },
                                  }))
                                }
                                className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                              />
                              <input
                                type="text"
                                placeholder="Σημείωση"
                                value={draft.description}
                                onChange={(e) =>
                                  setLineFormByGroup((lf) => ({
                                    ...lf,
                                    [g.id]: {
                                      ...draft,
                                      description: e.target.value,
                                    },
                                  }))
                                }
                                className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                              />
                              <button
                                type="button"
                                onClick={() => handleAddGroupLine(g.id)}
                                className="w-full bg-indigo-600 text-white py-1.5 rounded text-sm font-medium"
                              >
                                Προσθήκη στη ομάδα
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                }
                const t = row.tx;
                const canDone =
                  t.account_id && t.amount != null && parseFloat(t.amount) > 0;
                const isDone =
                  t.is_done === true || t.is_done === 1 || t.is_done === "1";
                const isEditing = editingTransactionId === t.id;
                return (
                  <div
                    key={t.id}
                    className={`bg-white rounded-lg px-3 py-2 flex justify-between items-start border-l-4 ${t.type === "εισόδημα" ? "border-green-500" : "border-red-500"}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        {readOnlyMobile
                          ? !isDone && (
                              <span className="w-3 shrink-0 inline-block text-center text-xs text-gray-400">
                                ○
                              </span>
                            )
                          : !isDone && (
                              <span className="w-3 shrink-0 inline-block text-center text-xs">
                                ○
                              </span>
                            )}
                        <p className="font-semibold text-sm truncate">
                          {t.transfer_group_id && (
                            <span
                              className="text-indigo-600 mr-1"
                              title="Μεταφορά"
                            >
                              ↔
                            </span>
                          )}
                          {t.description || t.Category?.name}
                        </p>
                      </div>
                      {isEditing ? (
                        <div className="mt-2 space-y-2">
                          <select
                            value={editingTransactionForm.account_id}
                            onChange={(e) =>
                              setEditingTransactionForm((prev) => ({
                                ...prev,
                                account_id: e.target.value,
                              }))
                            }
                            className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                          >
                            <option value="">Λογαριασμός</option>
                            {accounts.map((acc) => (
                              <option key={acc.id} value={acc.id}>
                                {acc.name}
                              </option>
                            ))}
                          </select>
                          <input
                            type="number"
                            step="0.01"
                            placeholder="Ποσό"
                            value={editingTransactionForm.amount}
                            onChange={(e) =>
                              setEditingTransactionForm((prev) => ({
                                ...prev,
                                amount: e.target.value,
                              }))
                            }
                            className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                          />
                          {t.type === "έξοδο" && (
                            <input
                              type="date"
                              placeholder="Ημερομηνία λήξης"
                              value={editingTransactionForm.due_date}
                              onChange={(e) =>
                                setEditingTransactionForm((prev) => ({
                                  ...prev,
                                  due_date: e.target.value,
                                }))
                              }
                              min={monthStart}
                              max={monthEnd}
                              className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs text-gray-700"
                            />
                          )}
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleUpdateTransaction(t.id)}
                              className="flex-1 bg-blue-600 text-white py-1.5 rounded text-sm font-medium"
                            >
                              Αποθήκευση
                            </button>
                            <button
                              onClick={() => setEditingTransactionId(null)}
                              className="flex-1 bg-gray-300 text-gray-700 py-1.5 rounded text-sm font-medium"
                            >
                              Ακύρωση
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <p className="text-xs text-gray-600">
                            {t.Category?.name}
                            {t.Account?.name
                              ? ` · ${t.Account.name}`
                              : t.account_id
                                ? ""
                                : " · Χωρίς λογαριασμό"}
                          </p>
                          <p className="flex items-baseline justify-between gap-2 mt-0.5">
                            <span
                              className={`text-sm font-bold ${t.type === "εισόδημα" ? "text-green-600" : "text-red-600"}`}
                            >
                              {t.type === "εισόδημα" ? "+" : "-"}
                              {formatEur(t.amount)}
                            </span>
                            {t.due_date &&
                              !isDone &&
                              (() => {
                                const today = new Date();
                                today.setHours(0, 0, 0, 0);
                                const due = new Date(t.due_date);
                                due.setHours(0, 0, 0, 0);
                                const diffDays = Math.ceil(
                                  (due - today) / (1000 * 60 * 60 * 24),
                                );
                                const isOverdueOrToday = diffDays <= 0;
                                const isWithin3Days =
                                  diffDays > 0 && diffDays <= 3;
                                const dueDateClass = isOverdueOrToday
                                  ? "text-red-600 font-bold"
                                  : isWithin3Days
                                    ? "text-orange-500 font-medium"
                                    : "text-gray-500";
                                return (
                                  <span
                                    className={`text-xs whitespace-nowrap ${dueDateClass}`}
                                  >
                                    📅{" "}
                                    {new Date(t.due_date).toLocaleDateString(
                                      "el-GR",
                                    )}
                                  </span>
                                );
                              })()}
                          </p>
                        </>
                      )}
                    </div>
                    {!isEditing && (
                      <div className="flex items-center gap-1.5 shrink-0">
                        {readOnlyMobile && isDone && (
                          <Check
                            className={`w-5 h-5 shrink-0 ${
                              t.type === "έξοδο"
                                ? "text-red-600"
                                : "text-green-600"
                            }`}
                            strokeWidth={2.5}
                            aria-label={
                              t.type === "έξοδο"
                                ? "Ολοκληρωμένο έξοδο"
                                : "Ολοκληρωμένο εισόδημα"
                            }
                          />
                        )}
                        {!readOnlyMobile && !isDone && (
                          <>
                            <button
                              type="button"
                              onClick={() => startEditTransaction(t)}
                              disabled={Boolean(t.transfer_group_id)}
                              className={`p-1.5 rounded ${
                                t.transfer_group_id
                                  ? "text-gray-300 cursor-not-allowed"
                                  : "hover:bg-gray-100 text-gray-500 hover:text-gray-700"
                              }`}
                              title={
                                t.transfer_group_id
                                  ? "Η μεταφορά δεν επεξεργάζεται εδώ"
                                  : "Επεξεργασία λογαριασμού / ποσού"
                              }
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            {(!t.amount || parseFloat(t.amount) === 0) && (
                              <button
                                onClick={() => deleteTransaction(t.id)}
                                className="p-1.5 hover:bg-red-50 rounded"
                                title="Διαγραφή"
                              >
                                <Trash2 className="w-4 h-4 text-red-600" />
                              </button>
                            )}
                          </>
                        )}
                        {!readOnlyMobile && isDone && t.transfer_group_id && (
                          <button
                            type="button"
                            onClick={() => deleteTransaction(t.id)}
                            className="p-1.5 hover:bg-red-50 rounded"
                            title="Διαγραφή μεταφοράς"
                          >
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </button>
                        )}
                        {!readOnlyMobile && (
                          <button
                            onClick={() => toggleTransactionDone(t.id)}
                            disabled={!isDone && !canDone}
                            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg font-medium text-sm transition ${
                              isDone
                                ? "bg-green-600 text-white hover:bg-green-700"
                                : canDone
                                  ? "bg-gray-200 text-gray-600 hover:bg-gray-300"
                                  : "bg-gray-100 text-gray-400 cursor-not-allowed"
                            }`}
                            title={
                              !isDone && !canDone
                                ? "Βάλτε λογαριασμό και ποσό πριν το σημειώσετε"
                                : ""
                            }
                          >
                            <Check
                              className="w-4 h-4 shrink-0"
                              strokeWidth={2.5}
                            />
                            {!isDone && <span>Σημείωσε</span>}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {mergedMonthRows.length > 0 && !readOnlyMobile && (
              <div className="flex flex-col gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddTransaction(!showAddTransaction);
                    if (!showAddTransaction) {
                      setShowTransfer(false);
                      setShowAddGroup(false);
                    }
                  }}
                  className="w-full bg-green-600 hover:bg-green-700 text-white py-3 px-4 rounded-lg font-bold flex items-center justify-center gap-2 text-sm whitespace-nowrap"
                >
                  <Plus className="w-5 h-5" /> Νέα Συναλλαγή
                </button>
                {accounts.length >= 2 && (
                  <button
                    type="button"
                    onClick={() => {
                      setShowTransfer(!showTransfer);
                      if (!showTransfer) {
                        setShowAddTransaction(false);
                        setShowAddGroup(false);
                        setTransferForm((prev) => ({
                          ...prev,
                          transaction_date:
                            prev.transaction_date || defaultTransferDate(),
                        }));
                      }
                    }}
                    className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-3 px-4 rounded-lg font-bold flex items-center justify-center gap-2 text-sm whitespace-nowrap"
                  >
                    <ArrowRightLeft className="w-5 h-5" />
                    Μεταφορά
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setShowAddGroup(!showAddGroup);
                    if (!showAddGroup) {
                      setShowAddTransaction(false);
                      setShowTransfer(false);
                    }
                  }}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white py-3 px-4 rounded-lg font-bold flex items-center justify-center gap-2 text-sm whitespace-nowrap"
                >
                  <Layers className="w-5 h-5" />
                  Νέα ομάδα
                </button>
              </div>
            )}

            {showTransfer && !readOnlyMobile && accounts.length >= 2 && (
              <div className="bg-white rounded-lg p-4 space-y-3 border-2 border-indigo-100">
                <p className="text-sm font-semibold text-indigo-900">
                  Μεταφορά χρημάτων
                </p>
                <select
                  value={transferForm.from_account_id}
                  onChange={(e) =>
                    setTransferForm((p) => ({
                      ...p,
                      from_account_id: e.target.value,
                    }))
                  }
                  className="w-full border border-gray-300 rounded px-3 py-2"
                >
                  <option value="">Από λογαριασμό</option>
                  {accounts.map((acc) => (
                    <option key={acc.id} value={acc.id}>
                      {acc.name}
                    </option>
                  ))}
                </select>
                <select
                  value={transferForm.to_account_id}
                  onChange={(e) =>
                    setTransferForm((p) => ({
                      ...p,
                      to_account_id: e.target.value,
                    }))
                  }
                  className="w-full border border-gray-300 rounded px-3 py-2"
                >
                  <option value="">Προς λογαριασμό</option>
                  {accounts.map((acc) => (
                    <option key={acc.id} value={acc.id}>
                      {acc.name}
                    </option>
                  ))}
                </select>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="Ποσό"
                  value={transferForm.amount}
                  onChange={(e) =>
                    setTransferForm((p) => ({ ...p, amount: e.target.value }))
                  }
                  className="w-full border border-gray-300 rounded px-3 py-2"
                />
                <input
                  type="date"
                  value={transferForm.transaction_date || defaultTransferDate()}
                  onChange={(e) =>
                    setTransferForm((p) => ({
                      ...p,
                      transaction_date: e.target.value,
                    }))
                  }
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                />
                <input
                  type="text"
                  placeholder="Σημείωση (προαιρετικό)"
                  value={transferForm.description}
                  onChange={(e) =>
                    setTransferForm((p) => ({
                      ...p,
                      description: e.target.value,
                    }))
                  }
                  className="w-full border border-gray-300 rounded px-3 py-2"
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleTransfer}
                    className="flex-1 bg-indigo-600 text-white py-2 rounded font-bold"
                  >
                    Εκτέλεση μεταφοράς
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowTransfer(false)}
                    className="flex-1 bg-gray-300 text-gray-700 py-2 rounded font-bold"
                  >
                    Ακύρωση
                  </button>
                </div>
              </div>
            )}

            {showAddGroup && !readOnlyMobile && (
              <div className="bg-white rounded-lg p-4 space-y-3 border-2 border-purple-100">
                <p className="text-sm font-semibold text-purple-900">
                  Ομαδοποιημένη δαπάνη / εισόδημα
                </p>
                {groupFixedTemplates.length > 0 && (
                  <select
                    value={groupForm.fixed_item_template_id}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (!v) {
                        setGroupForm((f) => ({
                          ...f,
                          fixed_item_template_id: "",
                        }));
                        return;
                      }
                      const item = groupFixedTemplates.find(
                        (x) => String(x.id) === v,
                      );
                      if (!item) return;
                      setGroupForm((f) => ({
                        ...f,
                        fixed_item_template_id: v,
                        name: item.description || "",
                        type: item.type || "έξοδο",
                        category_id: item.category_id
                          ? String(item.category_id)
                          : "",
                      }));
                    }}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  >
                    <option value="">Από πάγιο ομαδικό (προαιρετικό)…</option>
                    {groupFixedTemplates.map((it) => (
                      <option key={it.id} value={String(it.id)}>
                        {it.description}
                        {it.Account?.name
                          ? ` · ${it.Account.name}`
                          : " · χωρίς λογαριασμό"}
                      </option>
                    ))}
                  </select>
                )}
                <input
                  type="text"
                  placeholder="Όνομα (π.χ. Σουπερ μάρκετ, Καφές)"
                  value={groupForm.name}
                  onChange={(e) =>
                    setGroupForm((f) => ({ ...f, name: e.target.value }))
                  }
                  className="w-full border border-gray-300 rounded px-3 py-2"
                />
                <select
                  value={groupForm.type}
                  onChange={(e) =>
                    setGroupForm((f) => ({
                      ...f,
                      type: e.target.value,
                      category_id: "",
                    }))
                  }
                  className="w-full border border-gray-300 rounded px-3 py-2"
                >
                  <option value="έξοδο">Έξοδο</option>
                  <option value="εισόδημα">Εισόδημα</option>
                </select>
                <select
                  value={groupForm.category_id}
                  onChange={(e) =>
                    setGroupForm((f) => ({ ...f, category_id: e.target.value }))
                  }
                  className="w-full border border-gray-300 rounded px-3 py-2"
                >
                  <option value="">Κατηγορία</option>
                  {categories[groupForm.type]
                    ?.filter((cat) => cat.name !== "Μεταφορά λογαριασμών")
                    .map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleCreateGroup}
                    className="flex-1 bg-purple-600 text-white py-2 rounded font-bold"
                  >
                    Δημιουργία ομάδας
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowAddGroup(false)}
                    className="flex-1 bg-gray-300 text-gray-700 py-2 rounded font-bold"
                  >
                    Ακύρωση
                  </button>
                </div>
              </div>
            )}

            {showAddTransaction && !readOnlyMobile && (
              <div className="bg-white rounded-lg p-4 space-y-3">
                {transactionFixedTemplates.length > 0 && (
                  <select
                    value={transactionForm.fixed_item_template_id}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (!v) {
                        setTransactionForm((f) => ({
                          ...f,
                          fixed_item_template_id: "",
                        }));
                        return;
                      }
                      const item = transactionFixedTemplates.find(
                        (x) => String(x.id) === v,
                      );
                      if (!item) return;
                      setTransactionForm((f) => ({
                        ...f,
                        fixed_item_template_id: v,
                        description: item.description || "",
                        type: item.type || "έξοδο",
                        category_id: item.category_id
                          ? String(item.category_id)
                          : "",
                        account_id: item.account_id
                          ? String(item.account_id)
                          : "",
                        amount: item.amount ? String(item.amount) : "",
                      }));
                    }}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  >
                    <option value="">Από πάγιο (προαιρετικό)…</option>
                    {transactionFixedTemplates.map((it) => (
                      <option key={it.id} value={String(it.id)}>
                        {it.description}
                        {it.Account?.name
                          ? ` · ${it.Account.name}`
                          : " · χωρίς λογαριασμό"}
                      </option>
                    ))}
                  </select>
                )}
                <select
                  value={transactionForm.account_id}
                  onChange={(e) =>
                    setTransactionForm({
                      ...transactionForm,
                      account_id: e.target.value,
                    })
                  }
                  className="w-full border border-gray-300 rounded px-3 py-2"
                >
                  <option value="">Λογαριασμός</option>
                  {accounts.map((acc) => (
                    <option key={acc.id} value={acc.id}>
                      {acc.name}
                    </option>
                  ))}
                </select>

                <select
                  value={transactionForm.type}
                  onChange={(e) =>
                    setTransactionForm({
                      ...transactionForm,
                      type: e.target.value,
                    })
                  }
                  className="w-full border border-gray-300 rounded px-3 py-2"
                >
                  <option value="έξοδο">Έξοδο</option>
                  <option value="εισόδημα">Εισόδημα</option>
                </select>

                <select
                  value={transactionForm.category_id}
                  onChange={(e) =>
                    setTransactionForm({
                      ...transactionForm,
                      category_id: e.target.value,
                    })
                  }
                  className="w-full border border-gray-300 rounded px-3 py-2"
                >
                  <option value="">Κατηγορία</option>
                  {categories[transactionForm.type]
                    ?.filter((cat) => cat.name !== "Μεταφορά λογαριασμών")
                    .map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>

                <input
                  type="number"
                  step="0.01"
                  placeholder="Ποσό"
                  value={transactionForm.amount}
                  onChange={(e) =>
                    setTransactionForm({
                      ...transactionForm,
                      amount: e.target.value,
                    })
                  }
                  className="w-full border border-gray-300 rounded px-3 py-2"
                />

                <input
                  type="text"
                  placeholder="Περιγραφή"
                  value={transactionForm.description}
                  onChange={(e) =>
                    setTransactionForm({
                      ...transactionForm,
                      description: e.target.value,
                    })
                  }
                  className="w-full border border-gray-300 rounded px-3 py-2"
                />

                <div className="flex gap-2">
                  <button
                    onClick={handleAddTransaction}
                    className="flex-1 bg-blue-600 text-white py-2 rounded font-bold"
                  >
                    Αποθήκευση
                  </button>
                  <button
                    onClick={() => setShowAddTransaction(false)}
                    className="flex-1 bg-gray-300 text-gray-700 py-2 rounded font-bold"
                  >
                    Ακύρωση
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Fixed Items Tab */}
        {tab === "fixed" && !readOnlyMobile && (
          <div className="space-y-4">
            <div className="space-y-2">
              {[...fixedItems]
                .sort((a, b) => {
                  const groupOrder = (item) =>
                    item.type === "εισόδημα" ? 0 : item.creates_group ? 1 : 2;
                  const ga = groupOrder(a),
                    gb = groupOrder(b);
                  if (ga !== gb) return ga - gb;
                  return a.description.localeCompare(b.description, "el");
                })
                .map((item) => {
                  const isExpanded = expandedFixedId === item.id;
                  return (
                    <div
                      key={item.id}
                      className={`rounded-lg border-l-4 ${
                        item.type === "εισόδημα"
                          ? "border-green-500"
                          : "border-red-500"
                      } ${item.is_active ? "bg-white" : "bg-gray-100 opacity-60"}`}
                    >
                      <div
                        className={`flex justify-between items-start px-3 py-2 ${!item.creates_group && item.is_active ? "cursor-pointer" : ""}`}
                        onClick={() =>
                          !item.creates_group &&
                          item.is_active &&
                          setExpandedFixedId(isExpanded ? null : item.id)
                        }
                      >
                        <div className="min-w-0">
                          <p className="font-semibold text-sm truncate flex items-center gap-2 flex-wrap">
                            {item.description}
                            {item.creates_group ? (
                              <span className="text-[10px] font-bold uppercase tracking-wide bg-purple-100 text-purple-800 px-1.5 py-0.5 rounded">
                                Ομάδα
                              </span>
                            ) : null}
                          </p>
                          <div className="flex items-baseline justify-between gap-2">
                            <p className="text-xs text-gray-600 truncate">
                              {item.Category?.name || "—"} · {item.frequency}
                              {item.type === "έξοδο" &&
                                !item.creates_group &&
                                item.reminder_date && (
                                  <span className="ml-1 text-blue-600">
                                    · {item.reminder_date}η
                                  </span>
                                )}
                            </p>
                            {item.amount && (
                              <p
                                className={`text-sm font-bold whitespace-nowrap ${item.type === "εισόδημα" ? "text-green-600" : "text-red-600"}`}
                              >
                                {item.type === "εισόδημα" ? "+" : "-"}
                                {formatEur(item.amount, true)}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          {!item.creates_group && item.is_active ? (
                            <ChevronDown
                              className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? "rotate-180" : ""}`}
                            />
                          ) : (
                            <span className="w-4 h-4" />
                          )}
                          {!readOnlyMobile && item.is_active && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleFixedItemActive(item);
                              }}
                              className="p-1.5 rounded transition bg-green-600 hover:bg-green-700 text-white"
                              title="Απενεργοποίηση"
                            >
                              <Check className="w-4 h-4" strokeWidth={3} />
                            </button>
                          )}
                          {!readOnlyMobile && !item.is_active && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleFixedItemActive(item);
                              }}
                              className="p-1.5 rounded transition bg-gray-300 hover:bg-gray-400 text-gray-700"
                              title="Ενεργοποίηση"
                            >
                              <Check className="w-4 h-4" strokeWidth={3} />
                            </button>
                          )}
                          {!readOnlyMobile && !item.is_active && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteFixedItem(item.id);
                              }}
                              className="p-1.5 rounded transition bg-red-100 hover:bg-red-200 text-red-600"
                              title="Διαγραφή"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>

                      {isExpanded && (
                        <div className="px-3 pb-3 pt-1 border-t border-gray-100 space-y-2">
                          {!item.creates_group && (
                            <>
                              <div className="flex items-center gap-2">
                                <label className="text-xs text-gray-500 w-16">
                                  Ποσό
                                </label>
                                <input
                                  type="number"
                                  step="0.01"
                                  defaultValue={item.amount ?? ""}
                                  onBlur={(e) => {
                                    const val = e.target.value;
                                    const newAmount =
                                      val === "" ? null : parseFloat(val);
                                    if (
                                      newAmount !==
                                      (item.amount
                                        ? parseFloat(item.amount)
                                        : null)
                                    ) {
                                      updateFixedItemField(item.id, {
                                        amount: newAmount,
                                      });
                                    }
                                  }}
                                  className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm"
                                />
                              </div>
                              {item.type === "έξοδο" && (
                                <div className="flex items-center gap-2">
                                  <label className="text-xs text-gray-500 w-16">
                                    Ημέρα
                                  </label>
                                  <select
                                    defaultValue={item.reminder_date ?? ""}
                                    onChange={(e) => {
                                      const val = e.target.value;
                                      const newVal =
                                        val === "" ? null : parseInt(val);
                                      if (newVal !== item.reminder_date) {
                                        updateFixedItemField(item.id, {
                                          reminder_date: newVal,
                                        });
                                      }
                                    }}
                                    className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm"
                                  >
                                    <option value="">—</option>
                                    {Array.from(
                                      { length: 30 },
                                      (_, i) => i + 1,
                                    ).map((d) => (
                                      <option key={d} value={d}>
                                        {d}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>

            {!readOnlyMobile && (
              <button
                onClick={() => setShowAddFixed(!showAddFixed)}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-bold flex items-center justify-center gap-2"
              >
                <Plus className="w-5 h-5" /> Νέο Πάγιο
              </button>
            )}

            {showAddFixed && !readOnlyMobile && (
              <div className="bg-white rounded-lg p-4 space-y-3">
                <select
                  value={fixedForm.account_id}
                  onChange={(e) =>
                    setFixedForm({ ...fixedForm, account_id: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded px-3 py-2"
                >
                  <option value="">Λογαριασμός (Προαιρετικό)</option>
                  {accounts.map((acc) => (
                    <option key={acc.id} value={acc.id}>
                      {acc.name}
                    </option>
                  ))}
                </select>

                <select
                  value={fixedForm.type}
                  onChange={(e) =>
                    setFixedForm({ ...fixedForm, type: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded px-3 py-2"
                >
                  <option value="έξοδο">Έξοδο</option>
                  <option value="εισόδημα">Εισόδημα</option>
                </select>

                <select
                  value={fixedForm.category_id}
                  onChange={(e) =>
                    setFixedForm({ ...fixedForm, category_id: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded px-3 py-2"
                >
                  <option value="">Κατηγορία</option>
                  {categories[fixedForm.type]?.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>

                <input
                  type="text"
                  placeholder="Περιγραφή"
                  value={fixedForm.description}
                  onChange={(e) =>
                    setFixedForm({ ...fixedForm, description: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded px-3 py-2"
                />

                <select
                  value={fixedForm.frequency}
                  onChange={(e) =>
                    setFixedForm({ ...fixedForm, frequency: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded px-3 py-2"
                >
                  <option value="μηνιαία">Μηνιαία</option>
                  <option value="3μηνιαία">Ανά 3 μήνες</option>
                  <option value="ετήσια">Ετήσια</option>
                </select>

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={fixedForm.creates_group}
                    onChange={(e) =>
                      setFixedForm({
                        ...fixedForm,
                        creates_group: e.target.checked,
                        ...(e.target.checked
                          ? { amount_required: false, amount: "" }
                          : {}),
                      })
                    }
                  />
                  <span className="text-sm">Ομαδική δαπάνη</span>
                </label>

                {!fixedForm.creates_group && (
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={fixedForm.amount_required}
                      onChange={(e) =>
                        setFixedForm({
                          ...fixedForm,
                          amount_required: e.target.checked,
                        })
                      }
                    />
                    <span className="text-sm">Έχει σταθερό ποσό</span>
                  </label>
                )}

                {!fixedForm.creates_group && fixedForm.amount_required && (
                  <input
                    type="number"
                    step="0.01"
                    placeholder="Ποσό"
                    value={fixedForm.amount}
                    onChange={(e) =>
                      setFixedForm({ ...fixedForm, amount: e.target.value })
                    }
                    className="w-full border border-gray-300 rounded px-3 py-2"
                  />
                )}

                <div className="flex gap-2">
                  <button
                    onClick={handleAddFixed}
                    className="flex-1 bg-blue-600 text-white py-2 rounded font-bold"
                  >
                    Αποθήκευση
                  </button>
                  <button
                    onClick={() => setShowAddFixed(false)}
                    className="flex-1 bg-gray-300 text-gray-700 py-2 rounded font-bold"
                  >
                    Ακύρωση
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Categories Tab */}
        {tab === "categories" && !readOnlyMobile && (
          <div className="space-y-4">
            {["εισόδημα", "έξοδο"].map((type) => (
              <div key={type} className="bg-white rounded-lg p-4">
                <h3 className="font-bold mb-3">
                  {type === "έξοδο" ? "📊 Έξοδα" : "📈 Εισοδήματα"}
                </h3>
                <div className="space-y-2 mb-4">
                  {categories[type]?.map((cat) => (
                    <div
                      key={cat.id}
                      className="px-3 py-2 bg-gray-50 rounded text-sm"
                    >
                      {cat.name}
                    </div>
                  ))}
                </div>
                {!readOnlyMobile && (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="Νέα κατηγορία"
                      value={newCategory}
                      onChange={(e) => setNewCategory(e.target.value)}
                      className="flex-1 border border-gray-300 rounded px-3 py-2 text-sm"
                    />
                    <button
                      onClick={() => handleAddCategory(type)}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded font-bold"
                    >
                      +
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
